import { useMemo } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { RouterProvider, type RouterProviderProps } from 'react-router-dom';
import { store } from '../store';
import { buildTheme } from './theme';
import { getOrgBranding } from '../api/branding';
import { selectCurrentUser, selectIsAuthenticated } from '../store/slices/authSlice';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { ToastProvider } from '../components/common/ToastProvider';
import OfflineIndicator from '../components/common/OfflineIndicator';
import MeasurementPreferencesSync from '../components/common/MeasurementPreferencesSync';
import { DarkModeProvider, useDarkMode } from '../contexts/DarkModeContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

type AppRouter = RouterProviderProps['router'];

function AppContent({ router }: { router: AppRouter }) {
  const { darkMode } = useDarkMode();
  const user = useSelector(selectCurrentUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const orgId = user?.organizationId ?? null;

  // SS-DEC-005: per-org white-label palette. Fetched once authenticated; on
  // error/absent it falls back to the default Sentry Solutions theme.
  const { data: branding } = useQuery({
    queryKey: ['org-branding', orgId],
    queryFn: () => getOrgBranding(orgId as string),
    enabled: Boolean(isAuthenticated && orgId),
    staleTime: 5 * 60_000,
  });

  const theme = useMemo(
    () => buildTheme(darkMode ? 'dark' : 'light', branding ?? null),
    [darkMode, branding],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <OfflineIndicator />
      {/* Effective metric / imperial display units (decision S4). */}
      <MeasurementPreferencesSync />
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </ThemeProvider>
  );
}

export default function App({ router }: { router: AppRouter }) {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <DarkModeProvider>
            <AppContent router={router} />
          </DarkModeProvider>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>
  );
}
